import type { TMessageBase } from "./types";
import {
  createPathParamCRUD,
  createPathParamNestedGet,
  getPathParam,
  getPaginatedFilteredPathParam,
} from "@/api/shared/crud";
import type { DefaultPaginatedFilters } from "@/api/shared/filters";
import type { TPracticeStaff } from "@/api/models/practice/practiceStaff";
import {
  MessageTypeEnum as EMessageTypes,
  MessageAttachmentTypeEnum as EAttachmentType,
  PriorityEnum as EPriority,
} from "@/api/generated/messages.types";
import { createService } from "@/api/shared/service";

export * from "./types";

export { EAttachmentType, EPriority, EMessageTypes };

type TThreadFields = {
  replies_count: number;
  last_reply: string;
  replies: TMessageBase[];
};

export type TMessageDetail = TMessageBase &
  TThreadFields & {
    reply_to: TMessageBase; // BE returns expanded object
  };

export type TCreateMessageDetail = TMessageBase &
  TThreadFields & {
    reply_to: string; // create sends ID
  };

export type TMessageWithStaff = {
  message: TMessageDetail;
  staff: TPracticeStaff;
};

export type TMessageInfiniteQuery = {
  staff: TPracticeStaff;
  messages: TMessageDetail[];
  last_read_at: string | null;
};

export const NON_SYSTEM_MESSAGES = [
  EMessageTypes.URGENT,
  EMessageTypes.DELAY,
  EMessageTypes.FOLLOW_UP,
  EMessageTypes.AWAITED_INPUT,
  EMessageTypes.MESSAGE,
  EMessageTypes.STATUS_UPDATE,
];

const SLUG = "message/room";

type RoomParams = { roomId: string };
const ROOM_SLUG_TEMPLATE = `${SLUG}/{roomId}`;
const MESSAGES_SLUG_TEMPLATE = `${ROOM_SLUG_TEMPLATE}/messages`;

export type MessageParams = RoomParams & { id: string };

export interface IMessageFilter extends DefaultPaginatedFilters, MessageParams {
  message_type?: EMessageTypes;
  message_type__in?: EMessageTypes[];
}

export default createService(MESSAGES_SLUG_TEMPLATE, {
  ...createPathParamCRUD<
    TMessageDetail,
    Omit<MessageParams, "id">,
    IMessageFilter,
    Partial<TCreateMessageDetail>
  >(MESSAGES_SLUG_TEMPLATE),

  getMessagesPaginatedFiltered: getPaginatedFilteredPathParam<
    TMessageInfiniteQuery,
    RoomParams,
    IMessageFilter
  >(MESSAGES_SLUG_TEMPLATE),

  getMessage: getPathParam<TMessageWithStaff, MessageParams>(`${MESSAGES_SLUG_TEMPLATE}/{id}`),

  markReadMessage: createPathParamNestedGet<void, MessageParams>(
    MESSAGES_SLUG_TEMPLATE,
    "mark-read"
  ),

  unreadMessage: getPathParam<TMessageDetail, RoomParams>(`${ROOM_SLUG_TEMPLATE}/unread`),
});
